import { Check, ChevronDown } from "lucide-react";
import { Select as SelectPrimitive } from "radix-ui";
import type { ComponentProps, ReactNode } from "react";

import { appConfig } from "@/config/app";
import { cx } from "@/lib/cx";

import { Label, describedBy, inputClass, useFieldIds } from "./Field";
import fieldStyles from "./Field.module.css";
import floating from "./Floating.module.css";
import styles from "./Select.module.css";

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<
  ComponentProps<typeof SelectPrimitive.Root>,
  "children" | "dir"
> {
  label: string;
  options: readonly SelectOption[];
  placeholder?: string;
  description?: ReactNode;
  hideLabel?: boolean;
  id?: string;
  className?: string | undefined;
}

/** One choice from a short, fixed list. */
export function Select({
  label,
  options,
  placeholder,
  description,
  hideLabel = false,
  id,
  className,
  ...rootProps
}: SelectProps) {
  const ids = useFieldIds(id);
  return (
    <div className={cx(fieldStyles.field, className)}>
      <Label htmlFor={ids.controlId} hidden={hideLabel}>
        {label}
      </Label>
      <SelectPrimitive.Root {...rootProps}>
        <SelectPrimitive.Trigger
          id={ids.controlId}
          aria-describedby={describedBy(ids, description !== undefined, false)}
          className={cx(inputClass, styles.trigger)}
        >
          <SelectPrimitive.Value {...(placeholder === undefined ? {} : { placeholder })} />
          <SelectPrimitive.Icon className={styles.chevron}>
            <ChevronDown aria-hidden="true" />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
        <SelectPrimitive.Portal>
          <SelectPrimitive.Content
            position="popper"
            sideOffset={appConfig.overlayOffsetPx}
            className={cx(floating.panel, styles.content, "animate-in")}
          >
            <SelectPrimitive.Viewport className={floating.list}>
              {options.map((option) => (
                <SelectPrimitive.Item
                  key={option.value}
                  value={option.value}
                  {...(option.disabled === undefined ? {} : { disabled: option.disabled })}
                  className={cx(floating.item, floating.indented)}
                >
                  <SelectPrimitive.ItemIndicator className={floating.indicator}>
                    <Check aria-hidden="true" />
                  </SelectPrimitive.ItemIndicator>
                  <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                </SelectPrimitive.Item>
              ))}
            </SelectPrimitive.Viewport>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
      {description !== undefined && (
        <p id={ids.descriptionId} className={fieldStyles.description}>
          {description}
        </p>
      )}
    </div>
  );
}
